import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "reactstrap";
import { X } from "react-feather";
import { teacherSlotDetails, addStduentOffline, getStudentsOfflineNew, removeStudentOffline } from "../../../Api/apiUtils";
import { toast } from "react-toastify";
import ClickToCall from "../../../Telephony/ClickToCall";
import {
  Button,
  Badge,
  DescriptionList,
  Field,
  SearchInput,
  Loader,
  EmptyState,
} from "../../../ui";
import "./batchDetailsModal.scss";


// Confirmation Modal Component
interface ConfirmationModalProps {
  isOpen: boolean;
  toggle: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  toggle,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmColor = "danger"
}) => {
  const handleConfirm = () => {
    onConfirm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="ds-dialog">
      <h2 className="ds-dialog__title">{title}</h2>
      <div className="ds-dialog__body">
        <p>{message}</p>
      </div>
      <div className="ds-dialog__actions">
        <Button variant="secondary" onClick={toggle}>
          {cancelText}
        </Button>
        <Button variant={confirmColor === "danger" ? "danger" : "primary"} onClick={handleConfirm} autoFocus>
          {confirmText}
        </Button>
      </div>
    </Modal>
  );
};

type Batch = {
  id: string;
  teacher: string;
  level: string | any[];
  ageGroup: string;
  students: string;
  subject?: string;
  room?: string;
  description?: string;
  teacherId?: number;
  centerId?: number;
  studentCount?: number;

};

type SlotDetails = {
  teacherName?: string;
  slot: {
    id: number;
    teacher:any;
    teacherId: number;
    slotDate: string;
    startTime: string;
    endTime: string;

    offlineCenter: {
      id: number;
      name: string;
      city: string;
      state: string;
      country: string;
      pincode: string;
    };
    available: boolean;
  };
  students: Array<{
    id: number;
    name: string;
  }>;
};

interface BatchDetailsModalProps {
  isOpen: boolean;
  toggle: () => void;
  selectedBatch: Batch | null;
  selectedCourse:any
}

const BatchDetailsModal: React.FC<BatchDetailsModalProps> = ({
  isOpen,
  toggle,
  selectedBatch,
  selectedCourse,
}) => {
  console.log("selectedCourse",selectedCourse)
  const [slotDetails, setSlotDetails] = useState<SlotDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableStudents, setAvailableStudents] = useState<any[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [addingStudent, setAddingStudent] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);


  useEffect(() => {
    if (isOpen && selectedBatch) {
      fetchSlotDetails();
    } else if (!isOpen) {
      // Reset form when modal is closed
      setSelectedStudent(null);
      setShowAddStudent(false);
      setShowConfirmModal(false);
      setStudentToDelete(null);
      setSearchQuery("");
      setAvailableStudents([]);
    }
  }, [isOpen, selectedBatch]);

  const fetchSlotDetails = async () => {
    if (!selectedBatch) return;
    
    setLoading(true);
    setError(null);
    try {
      const details = await teacherSlotDetails(selectedBatch.id);
      setSlotDetails(details);
    } catch (err) {
      setError("Failed to fetch slot details");
      console.error("Error fetching slot details:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableStudents = async (searchTerm: string = "") => {
    if (!selectedBatch) return;
    
    setIsSearching(true);
    try {
      const response = await getStudentsOfflineNew(selectedCourse, selectedBatch.centerId, searchTerm);
      console.log("Full API Response:", response);
      console.log("Search term was:", searchTerm);
      
      // Handle different response structures
      let students = [];
      
      // Check if response has content array
      if (response && response.content && Array.isArray(response.content)) {
        students = response.content;
        console.log("Using response.content:", students);
      } 
      // Check if response is directly an array
      else if (Array.isArray(response)) {
        students = response;
        console.log("Using direct array:", students);
      } 
      // Check if response has data property
      else if (response && response.data) {
        if (Array.isArray(response.data)) {
          students = response.data;
        } else if (response.data.content && Array.isArray(response.data.content)) {
          students = response.data.content;
        } else {
          students = [];
        }
        console.log("Using response.data:", students);
      } 
      // Check if response has students property
      else if (response && response.students && Array.isArray(response.students)) {
        students = response.students;
        console.log("Using response.students:", students);
      }
      // Check if response has results property
      else if (response && response.results && Array.isArray(response.results)) {
        students = response.results;
        console.log("Using response.results:", students);
      }
      else {
        console.log("No valid data structure found in response");
        console.log("Response keys:", Object.keys(response || {}));
      }
      
      console.log("Final extracted students:", students);
      console.log("Number of students:", students.length);
      
      // Force update the state even if empty to trigger re-render
      setAvailableStudents(students);
    } catch (err) {
      console.error("Error fetching students:", err);
      // toast.error("Failed to fetch students");
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchTerm: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchAvailableStudents(searchTerm);
        }, 500); // 500ms delay
      };
    })(),
    [selectedCourse, selectedBatch]
  );


  const handleAddStudent = async () => {
    console.log("slotDetails",slotDetails)
    if (!selectedStudent || !slotDetails || !selectedBatch) return;
    
    setAddingStudent(true);
    try {
      // Get day of week from slot date
      const slotDate = new Date(slotDetails.slot.slotDate);
      const dayOfWeek = slotDate.getDay() === 0 ? 7 : slotDate.getDay();
      
      const addStudentData = {
        studentId: selectedStudent.id,
        teacherId: slotDetails.slot.teacherId,
        courseId: selectedCourse, // Assuming courseId is the slot ID
        startTime: slotDetails.slot.startTime,
        endTime: slotDetails.slot.endTime,
        dayOfWeek: dayOfWeek
      };
      
      await addStduentOffline(addStudentData);
      
 
      await fetchSlotDetails();
      
      // Reset form
      setSelectedStudent(null);
      setShowAddStudent(false);
      
      toast.success("Student added successfully!");
    } catch (err: any) {
      console.error("Error adding student:", err);
      // Extract error message from the response
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to add student";
      toast.error(errorMessage);
    } finally {
      setAddingStudent(false);
    }
  };

  const handleDeleteStudent = (studentId: number) => {
    setStudentToDelete(studentId);
    setShowConfirmModal(true);
  };

  const confirmDeleteStudent = async () => {
    if (!studentToDelete || !slotDetails) return;
    
    try {
      if (!slotDetails.slot.slotDate) {
        console.error('Slot date is not available');
        return;
      }
      const slotDate = new Date(slotDetails.slot.slotDate);
      const dayOfWeek = slotDate.getDay() === 0 ? 7 : slotDate.getDay();
      
      const addStudentData = {
        studentId: studentToDelete,
        teacherId: slotDetails.slot.teacherId,
        dayOfWeek: dayOfWeek,
        startTime: slotDetails.slot.startTime,
        templateId:slotDetails.slot.id
        // endTime: slotDetails.slot.endTime,
      };
      
      await removeStudentOffline(addStudentData);
      
      console.log("Delete student:", studentToDelete);
      // Refresh the data
      await fetchSlotDetails();
      
      toast.success("Student removed successfully!");
    } catch (err: any) {
      console.error("Error deleting student:", err);
      // Extract error message from the response
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to remove student";
      toast.error(errorMessage);
    } finally {
      setStudentToDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };


  const notEnrolledStudents = availableStudents.filter(student => {
    // Filter out students who are already enrolled in this batch
    if (!slotDetails?.students || !Array.isArray(slotDetails.students)) {
      return true;
    }
    return !slotDetails.students.some(enrolledStudent =>
      enrolledStudent.id === student.id
    );
  });

  const ageGroups = selectedBatch?.ageGroup
    ? selectedBatch.ageGroup
        .split(",")
        .map(g => g.trim())
        .filter(g => g !== "not_mentioned") // remove unwanted
    : [];

  const renderLevel = () => {
    if (!selectedBatch?.level) return null;
    if (!Array.isArray(selectedBatch.level)) return selectedBatch.level;
    if (selectedBatch.level.length > 1) {
      return (
        <span className="batch-details__levels" title={selectedBatch.level.join(", ")}>
          {selectedBatch.level
            .filter(level => level && level.trim() !== '')
            .map((level, index) => (
              <Badge key={index} tone="info">{level}</Badge>
            ))}
        </span>
      );
    }
    return selectedBatch.level[0] && selectedBatch.level[0].trim() !== '' ? selectedBatch.level[0] : '-';
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="ds-dialog batch-details">
      <div className="batch-details__header">
        <h2 className="ds-dialog__title">Slot Details</h2>
        <Button variant="ghost" size="sm" icon={<X size={16} aria-hidden />} onClick={toggle} aria-label="Close" />
      </div>
      <div className="batch-details__body">
        {loading ? (
          <Loader label="Loading slot details..." />
        ) : error ? (
          <EmptyState
            title={error}
            description="The slot couldn't be loaded. Try again."
            action={
              <Button variant="secondary" onClick={fetchSlotDetails}>
                Retry
              </Button>
            }
          />
        ) : slotDetails ? (
          <div className="ds-stack batch-details__content">
            {/* Slot Information */}
            <DescriptionList
              items={[
                { label: "Date", value: formatDate(slotDetails.slot.slotDate) },
                {
                  label: "Time",
                  value: `${formatTime(slotDetails.slot.startTime)} - ${formatTime(slotDetails.slot.endTime)}`,
                },
                { label: "Center", value: slotDetails.slot.offlineCenter.name },
                { label: "Teacher Name", value: slotDetails.teacherName || 'N/A' },
                ...(ageGroups.length > 0 ? [{ label: "Age Group", value: ageGroups.join(", ") }] : []),
                ...(selectedBatch?.level ? [{ label: "Level", value: renderLevel() }] : []),
              ]}
            />

            {/* Students List */}
            <div className="ds-stack">
              <div className="batch-details__section-head">
                <h3 className="batch-details__section-title">
                  Enrolled Students ({slotDetails.students.length})
                </h3>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowAddStudent(!showAddStudent);
                    if (!showAddStudent && availableStudents.length === 0) {
                      fetchAvailableStudents(""); // Fetch all students initially
                    }
                  }}
                >
                  {showAddStudent ? "Cancel" : "Add Student"}
                </Button>
              </div>

              {/* Add Student Form */}
              {showAddStudent && (
                <div className="batch-details__add ds-stack">
                  <Field label="Search Student" id="studentSearch">
                    {(ctl) => (
                      <SearchInput
                        {...ctl}
                        placeholder="Search by name, email, or phone number..."
                        value={searchQuery}
                        onChange={(e) => {
                          const value = e.target.value;
                          console.log("Input changed to:", value);
                          setSearchQuery(value);
                          debouncedSearch(value);
                        }}
                      />
                    )}
                  </Field>
                  {isSearching && <Loader label="Searching..." />}

                  {/* Student Results List */}
                  {availableStudents.length > 0 && (
                    <ul className="batch-details__results">
                      {notEnrolledStudents.map((student, index) => {
                        const name = student.name || student.studentName || student.firstName || 'Unknown Student';
                        const email = student.email || student.emailId || '';
                        const mobileNumber = student.mobileNumber || student.phoneNumber || student.mobile || '';
                        const isSelected = selectedStudent?.id === student.id;

                        return (
                          <li
                            key={student.id || index}
                            onClick={() => setSelectedStudent(student)}
                            className={
                              isSelected
                                ? "batch-details__result batch-details__result--selected"
                                : "batch-details__result"
                            }
                          >
                            <span className="ds-strong">{name}</span>
                            <div onClick={(e) => e.stopPropagation()}>
                              <ClickToCall number={mobileNumber || undefined} name={name} sourceRecordId={student.id} />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {!isSearching && availableStudents.length === 0 && searchQuery.trim() && (
                    <EmptyState title="No students found matching your search." description="Try a different name, email or phone number." />
                  )}

                  {!isSearching && availableStudents.length === 0 && !searchQuery.trim() && (
                    <EmptyState title="No students found for this course." description="Search by name, email or phone number." />
                  )}

                  {!isSearching && availableStudents.length > 0 && notEnrolledStudents.length === 0 && (
                    <EmptyState title="All available students are already enrolled in this batch." />
                  )}

                  {selectedStudent && (
                    <div className="batch-details__selected">
                      <span className="ds-strong">Selected Student:</span>{" "}
                      {selectedStudent.name || selectedStudent.studentName || selectedStudent.firstName}
                    </div>
                  )}

                  <div className="batch-details__actions">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setShowAddStudent(false);
                        setSelectedStudent(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAddStudent}
                      disabled={!selectedStudent}
                      loading={addingStudent}
                    >
                      {addingStudent ? "Adding..." : "Add to Slot"}
                    </Button>
                  </div>
                </div>
              )}

              {slotDetails.students.length > 0 ? (
                <ul className="batch-details__enrolled">
                  {slotDetails.students.map((student) => (
                    <li key={student.id} className="batch-details__enrolled-row">
                      <div>
                        <span className="ds-strong">{student.name}</span>{" "}
                        <span className="ds-text-muted">(ID: {student.id})</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="batch-details__remove"
                        onClick={() => handleDeleteStudent(student.id)}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState
                  title="No students enrolled in this slot"
                  action={
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setShowAddStudent(true);
                        fetchAvailableStudents(""); // Fetch all students initially
                      }}
                    >
                      Add First Student
                    </Button>
                  }
                />
              )}
            </div>
          </div>
        ) : (
          <EmptyState title="No slot details available" />
        )}
      </div>
      {/* <ModalFooter>
        <Button color="primary" onClick={toggle}>
          Close
        </Button>
        <Button color="success" onClick={() => {
          // Add edit functionality here
          console.log("Edit slot:", slotDetails);
          toggle();
        }}>
          Edit Slot
        </Button>
      </ModalFooter> */}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        toggle={() => setShowConfirmModal(false)}
        onConfirm={confirmDeleteStudent}
        title="Remove Student"
        message="Are you sure you want to remove this student from the slot?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmColor="danger"
      />
    </Modal>
  );
};

export default BatchDetailsModal;
